// src/components/HistoricalReportModal.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface Report {
  id: number;
  date?: string;
  created_at?: string;
}

interface Props {
  dealerId: string;
  report: Report;
  onClose: () => void;
}

// Helpers
const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatCurrency = (val: number | string | null) => {
  if (val == null) return "N/A";
  const num = Number(val);
  if (isNaN(num)) return "N/A";
  return `$${num.toLocaleString("en-US")}`;
};

const formatPercentage = (val: number | string | null) => {
  if (val == null) return "N/A";
  const num = Number(val);
  if (isNaN(num)) return "N/A";
  return `${num.toLocaleString("en-US")}%`;
};

// Styles
const popupOverlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};
const popupContentStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  padding: "20px",
  width: "95%",
  maxWidth: "900px",
  height: "90vh",
  overflowY: "auto",
  position: "relative",
};
const closeButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: "10px",
  right: "10px",
  padding: "5px 10px",
  backgroundColor: "#e41e26",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};
const tabContainerStyle: React.CSSProperties = {
  display: "flex",
  borderBottom: "1px solid #ccc",
  marginBottom: "10px",
};
const tabStyle: React.CSSProperties = {
  flex: 1,
  padding: "10px",
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: "#f0f0f0",
};
const activeTabStyle: React.CSSProperties = {
  ...tabStyle,
  backgroundColor: "#e41e26",
  color: "#fff",
  fontWeight: "bold",
};
const mainCardStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  marginBottom: "20px",
  padding: "20px",
};
const headerStyle: React.CSSProperties = {
  textAlign: "center",
  color: "#e41e26",
  marginBottom: "15px",
  fontSize: "20px",
};
// Added missing styles
const tdStyle: React.CSSProperties = { padding: "8px", borderBottom: "1px solid #ccc" };
const sectionHeaderStyle: React.CSSProperties = {
  color: "#e41e26",
  borderBottom: "1px solid #e41e26",
  paddingBottom: "5px",
  marginTop: "15px",
  fontSize: "16px",
};

const HistoricalReportModal: React.FC<Props> = ({ dealerId, report, onClose }) => {
  const navigate = useNavigate();

  // Data states
  const [salesTrainingData, setSalesTrainingData] = useState<any[]>([]);
  const [technicalSupportData, setTechnicalSupportData] = useState<any>(null);
  const [inventoryAuditData, setInventoryAuditData] = useState<any[]>([]);
  const [marketConditionsData, setMarketConditionsData] = useState<any>(null);
  const [customerSatisfactionData, setCustomerSatisfactionData] = useState<any[]>([]);
  const [installationAuditData, setInstallationAuditData] = useState<any[]>([]);
  const [financialDataData, setFinancialDataData] = useState<any[]>([]);
  const [dealerSupportNeedsData, setDealerSupportNeedsData] = useState<any>(null);
  const [dealerMarketingSupportData, setDealerMarketingSupportData] = useState<any>(null);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [finalSummaryData, setFinalSummaryData] = useState<any>(null);

  // Lock background scroll
  useEffect(() => {
    document.body.classList.add("no-scroll");
    return () => document.body.classList.remove("no-scroll");
  }, []);

  // Fetch all sections
  useEffect(() => {
    const id = report.id;
    fetch(`/api/reports/${id}/sales-training`).then(r => r.json()).then(setSalesTrainingData).catch(() => setSalesTrainingData([]));
    fetch(`/api/reports/${id}/technical-support`).then(r => r.json()).then(data => setTechnicalSupportData(Array.isArray(data) && data.length > 0 ? data[0] : null)).catch(() => setTechnicalSupportData(null));
    fetch(`/api/reports/${id}/inventory-audit`).then(r => r.json()).then(setInventoryAuditData).catch(() => setInventoryAuditData([]));
    fetch(`/api/reports/${id}/market-conditions`).then(r => r.json()).then(data => setMarketConditionsData(Array.isArray(data) && data.length > 0 ? data[0] : null)).catch(() => setMarketConditionsData(null));
    fetch(`/api/reports/${id}/customer-satisfaction`).then(r => r.json()).then(setCustomerSatisfactionData).catch(() => setCustomerSatisfactionData([]));
    fetch(`/api/reports/${id}/installation-audit`).then(r => r.json()).then(setInstallationAuditData).catch(() => setInstallationAuditData([]));
    fetch(`/api/reports/${id}/financial-data`).then(r => r.json()).then(setFinancialDataData).catch(() => setFinancialDataData([]));
    fetch(`/api/reports/${id}/dealer-support-needs`).then(r => r.json()).then(setDealerSupportNeedsData).catch(() => setDealerSupportNeedsData(null));
    fetch(`/api/reports/${id}/dealer-marketing-support`).then(r => r.json()).then(setDealerMarketingSupportData).catch(() => setDealerMarketingSupportData(null));
    fetch(`/api/reports/${id}/feedback`).then(r => r.json()).then(setFeedbackData).catch(() => setFeedbackData(null));
    fetch(`/api/reports/${id}/final-summary`).then(r => r.json()).then(setFinalSummaryData).catch(() => setFinalSummaryData(null));
  }, [report.id]);

  // Tab states
  const [activeCard1Tab, setActiveCard1Tab] = useState("trainingAudit");
  const [activeCard2Tab, setActiveCard2Tab] = useState("customerSatisfaction");
  const [activeCard3Tab, setActiveCard3Tab] = useState("dealerSupportNeeds");

  // Content mapping skeleton (replace in next step)
  const contentMap: Record<string, { title: string; content: React.ReactNode }> = {
  trainingAudit: {
    title: "Sales Training",
    content: (
      <>
        <p><strong>Objective:</strong> Ensure dealer sales reps are trained on product features and mapping.</p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={sectionHeaderStyle}>Sales Rep Name</th>
              <th style={sectionHeaderStyle}>Training Level</th>
              <th style={sectionHeaderStyle}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {salesTrainingData.length > 0 ? salesTrainingData.map((rep, i) => (
              <tr key={i}>
                <td style={tdStyle}>{rep.name || "N/A"}</td>
                <td style={tdStyle}>{rep.training_level || "N/A"}</td>
                <td style={tdStyle}>{rep.notes || "N/A"}</td>
              </tr>
            )) : (
              <tr>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
              </tr>
            )}
          </tbody>
        </table>
      </>
    )
  },

  technicalSupport: {
    title: "Technical Support",
    content: (
      <div>
        <p><strong>Objective:</strong> Document dealer technical inquiries.</p>
        <div style={{ marginBottom: "10px" }}>
          <h4 style={sectionHeaderStyle}>Questions Asked</h4>
          <p>{technicalSupportData?.questions_asked || "N/A"}</p>
        </div>
        <div style={{ marginBottom: "10px" }}>
          <h4 style={sectionHeaderStyle}>Resolved</h4>
          <p>
            {technicalSupportData?.resolved !== undefined
              ? (technicalSupportData.resolved ? "Yes" : "No")
              : "N/A"}
          </p>
        </div>
          <div style={{ marginBottom: "10px" }}>
    <h4 style={sectionHeaderStyle}>Follow-Up Actions</h4>
    <p>
      {technicalSupportData?.meeting_scheduled !== undefined
        ? (technicalSupportData.meeting_scheduled ? "Yes" : "No")
        : "N/A"}
    </p>

    <h4 style={sectionHeaderStyle}>Date of Scheduled Meeting</h4>
    <p>
      {technicalSupportData?.meeting_date
        ? formatDate(technicalSupportData.meeting_date)
        : "N/A"}
    </p>
  </div>
      </div>
    )
  },

  inventoryAudit: {
    title: "Inventory Audit",
    content: (
      <>
        <p><strong>Objective:</strong> Audit dealer inventory and parts authenticity.</p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={sectionHeaderStyle}>Item</th>
              <th style={sectionHeaderStyle}>Quantity</th>
              <th style={sectionHeaderStyle}>Genuine?</th>
              <th style={sectionHeaderStyle}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {inventoryAuditData.length > 0 ? inventoryAuditData.map((item, i) => (
              <tr key={i}>
                <td style={tdStyle}>{item.item || "N/A"}</td>
                <td style={tdStyle}>{item.quantity ?? "N/A"}</td>
                <td style={tdStyle}>
                  {item.genuine !== undefined ? (item.genuine ? "Yes" : "No") : "N/A"}
                </td>
                <td style={tdStyle}>{item.notes || "N/A"}</td>
              </tr>
            )) : (
              <tr>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
              </tr>
            )}
          </tbody>
        </table>
      </>
    )
  },

  marketConditions: {
    title: "Market Conditions",
    content: (
      <div>
        <p><strong>Objective:</strong> Summarize local market trends.</p>
        <div style={{ marginBottom: "10px" }}>
          <h4 style={sectionHeaderStyle}>Crop Loss & Insurance</h4>
          <p>
            Loss: {marketConditionsData?.crop_loss != null ? formatPercentage(marketConditionsData.crop_loss) : "N/A"},  
            Insurance: {marketConditionsData?.crop_insurance === true ? "Yes" : marketConditionsData?.crop_insurance === false ? "No" : "N/A"}
          </p>
        </div>
        <div style={{ marginBottom: "10px" }}>
          <h4 style={sectionHeaderStyle}>Returns & Subsidies</h4>
          <p>Returns: {marketConditionsData?.returns != null ? formatPercentage(marketConditionsData.returns) : "N/A"}</p>
          <p>Subsidies: {marketConditionsData?.government_subsidies || "N/A"}</p>
        </div>
        <div>
          <h4 style={sectionHeaderStyle}>Market Share Breakdown</h4>
          <p>
            {marketConditionsData?.our_company_name || "Our Company"}:{" "}
            {marketConditionsData?.our_company_share != null
              ? formatPercentage(marketConditionsData.our_company_share)
              : "N/A"}
          </p>
          {[1,2,3].map(i => {
            const name = marketConditionsData?.[`competitor${i}_name`];
            const share = marketConditionsData?.[`competitor${i}_share`];
            return name ? (
              <p key={i}>
                {name}: {share != null ? formatPercentage(share) : "N/A"}
              </p>
            ) : null;
          })}
		  <p>Why Choose Us? {marketConditionsData?.why_choose_us ?? "N/A"}</p>
          <p>Why Competitors: {marketConditionsData?.why_choose_competitors || "N/A"}</p>
        </div>
      </div>
    )
  },

  customerSatisfaction: {
    title: "Customer Satisfaction",
    content: (
      <>
        <p><strong>Objective:</strong> Gauge customer happiness.</p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={sectionHeaderStyle}>Customer</th>
              <th style={sectionHeaderStyle}>Company</th>
              <th style={sectionHeaderStyle}>Product Rating</th>
              <th style={sectionHeaderStyle}>Service Rating</th>
              <th style={sectionHeaderStyle}>Comments</th>
            </tr>
          </thead>
          <tbody>
            {customerSatisfactionData.length > 0 ? customerSatisfactionData.map((c, i) => (
              <tr key={i}>
                <td style={tdStyle}>{c.customer_name || "N/A"}</td>
                <td style={tdStyle}>{c.company || "N/A"}</td>
                <td style={tdStyle}>
                  {typeof c.product_rating === "number"
                    ? Array.from({ length: 5 }).map((_, j) => (
                        <span key={j}>{j < c.product_rating ? "★" : "☆"}</span>
                      ))
                    : "N/A"}
                </td>
                <td style={tdStyle}>
                  {typeof c.dealer_service_rating === "number"
                    ? Array.from({ length: 5 }).map((_, j) => (
                        <span key={j}>{j < c.dealer_service_rating ? "★" : "☆"}</span>
                      ))
                    : "N/A"}
                </td>
                <td style={tdStyle}>{c.comments || "N/A"}</td>
              </tr>
            )) : (
              <tr>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
              </tr>
            )}
          </tbody>
        </table>
      </>
    )
  },

  installationAudit: {
    title: "Installation Audit",
    content: (
      <>
        <p><strong>Objective:</strong> Check installation compliance.</p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={sectionHeaderStyle}>Grower</th>
              <th style={sectionHeaderStyle}>Location</th>
              <th style={sectionHeaderStyle}>Guidelines Followed?</th>
              <th style={sectionHeaderStyle}>Issues</th>
            </tr>
          </thead>
          <tbody>
            {installationAuditData.length > 0 ? installationAuditData.map((i, idx) => (
              <tr key={idx}>
                <td style={tdStyle}>{i.grower_name || "N/A"}</td>
                <td style={tdStyle}>{i.installation_location || "N/A"}</td>
                <td style={tdStyle}>{i.guidelines_followed ? "Yes" : "No"}</td>
                <td style={tdStyle}>{i.issues_found || "N/A"}</td>
              </tr>
            )) : (
              <tr>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
                <td style={tdStyle}>N/A</td>
              </tr>
            )}
          </tbody>
        </table>
      </>
    )
  },

  financialData: {
    title: "Financial Data",
    content: (
      <>
        <p><strong>Objective:</strong> Review key financial metrics.</p>
        {financialDataData.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={tdStyle}>Annual Revenue</td>
                <td style={tdStyle}>{formatCurrency(financialDataData[0].annual_revenue)}</td>
              </tr>
              <tr>
                <td style={tdStyle}>Units Sold (12mo)</td>
                <td style={tdStyle}>{financialDataData[0].new_units_sold ?? "N/A"}</td>
              </tr>
              <tr>
                <td style={tdStyle}>Avg Sell Price</td>
                <td style={tdStyle}>{formatCurrency(financialDataData[0].avg_selling_price)}</td>
              </tr>
              <tr>
                <td style={tdStyle}>Profit Margin</td>
                <td style={tdStyle}>{formatPercentage(financialDataData[0].profit_margin)}</td>
              </tr>
			   <tr>
      <td style={tdStyle}>% of Total</td>
      <td style={tdStyle}>
        {financialDataData[0].percentage_of_total != null
          ? `${financialDataData[0].percentage_of_total}%`
          : "N/A"}
      </td>
    </tr>
    <tr>
      <td style={tdStyle}>Repeat Customers</td>
      <td style={tdStyle}>
        {financialDataData[0].num_repeat_customers ?? "N/A"}
      </td>
    </tr>
    <tr>
      <td style={tdStyle}>Total Parts Revenue</td>
      <td style={tdStyle}>
        {formatCurrency(financialDataData[0].total_parts_revenue)}
      </td>
    </tr>
    <tr>
      <td style={tdStyle}>Most Sold Models</td>
      <td style={tdStyle}>
        {financialDataData[0].most_sold_models || "N/A"}
      </td>
    </tr>
    <tr>
      <td style={tdStyle}>Avg Time to Close</td>
      <td style={tdStyle}>
        {financialDataData[0].avg_time_to_close ?? "N/A"}
      </td>
    </tr>
    <tr>
      <td style={tdStyle}>Top Customer Group</td>
      <td style={tdStyle}>
        {financialDataData[0].top_customer_group || "N/A"}
      </td>
    </tr>
            </tbody>
          </table>
        ) : (
          <p>N/A</p>
        )}
      </>
    )
  },

  dealerSupportNeeds: {
    title: "Support Needs",
    content: (
      <div>
        <p><strong>Objective:</strong> Identify dealer challenges.</p>
        <h4 style={sectionHeaderStyle}>Challenges</h4>
        <p>{dealerSupportNeedsData?.challenges || "N/A"}</p>
        <h4 style={sectionHeaderStyle}>Action Plan</h4>
        <p>{dealerSupportNeedsData?.action_plan || "N/A"}</p>
      </div>
    )
  },

  dealerMarketingSupport: {
    title: "Marketing Support",
    content: (
      <div>
        <p><strong>Objective:</strong> Assess marketing resources.</p>
        <h4 style={sectionHeaderStyle}>Materials Provided</h4>
        <p>{dealerMarketingSupportData?.marketing_materials || "N/A"}</p>
        <h4 style={sectionHeaderStyle}>Promotional Events</h4>
        <p>{dealerMarketingSupportData?.promotional_events || "N/A"}</p>
		<h4 style={sectionHeaderStyle}>Materials Provided</h4>
 <p>{dealerMarketingSupportData?.marketing_materials || "N/A"}</p>

 <h4 style={sectionHeaderStyle}>Outreach Channels</h4>
 <p>{dealerMarketingSupportData?.outreach_channels || "N/A"}</p>

 <h4 style={sectionHeaderStyle}>Additional Support</h4>
 <p>{dealerMarketingSupportData?.additional_support || "N/A"}</p>

 <h4 style={sectionHeaderStyle}>Promotional Events</h4>
 <p>{dealerMarketingSupportData?.promotional_events || "N/A"}</p>
      </div>
    )
  },

  feedback: {
    title: "Feedback & Insights",
    content: (
      <div>
        <p><strong>Objective:</strong> Capture grower feedback.</p>
         <h4 style={sectionHeaderStyle}>Customer Complaints</h4>
 <p>{feedbackData?.customer_complaints || "N/A"}</p>

 <h4 style={sectionHeaderStyle}>Desired Features</h4>
 <p>{feedbackData?.desired_features || "N/A"}</p>

 <h4 style={sectionHeaderStyle}>Service Issues</h4>
 <p>{feedbackData?.service_issues || "N/A"}</p>

 <h4 style={sectionHeaderStyle}>Escalation Details</h4>
 <p>{feedbackData?.escalation_details || "N/A"}</p>

 <h4 style={sectionHeaderStyle}>Insights</h4>
 <p>{feedbackData?.insights || "N/A"}</p>

 <h4 style={sectionHeaderStyle}>Recommendations</h4>
 <p>{feedbackData?.recommendations || "N/A"}</p>

      </div>
    )
  },

  finalSummary: {
    title: "Final Summary",
    content: (
      <div>
        <p><strong>Objective:</strong> Summarize overall findings & next steps.</p>
                 <p><strong>Overall Rating:</strong>{" "}
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i}>
              {i < (finalSummaryData?.overall_rating ?? 0) ? "★" : "☆"}
            </span>
          ))}
        </p>
 <p><strong>Key Takeaways:</strong> {finalSummaryData?.key_takeaways || "N/A"}</p>
 <p><strong>Representative Name:</strong> {finalSummaryData?.representative_name || "N/A"}</p>
 <p><strong>Summary Date:</strong> {finalSummaryData?.summary_date
     ? formatDate(finalSummaryData.summary_date)
     : "N/A"}</p>
 <p><strong>Summary:</strong> {finalSummaryData?.summary || "N/A"}</p>
      </div>
    )
  }
};


  const card1Tabs = ["trainingAudit", "technicalSupport", "inventoryAudit", "marketConditions"];
  const card2Tabs = ["customerSatisfaction", "installationAudit", "financialData"];
  const card3Tabs = ["dealerSupportNeeds", "dealerMarketingSupport", "feedback", "finalSummary"];

  return (
    <div style={popupOverlayStyle} onClick={onClose}>
      <div style={popupContentStyle} onClick={e => e.stopPropagation()}>
        <button style={closeButtonStyle} onClick={onClose}>Close</button>
        <h2 style={headerStyle}>Historical Report — {formatDate(report.created_at || report.date!)}</h2>

        {/* Card 1 */}
        <div style={mainCardStyle}>
          <div style={tabContainerStyle}>
            {card1Tabs.map(key => (
              <div key={key} onClick={() => setActiveCard1Tab(key)} style={activeCard1Tab === key ? activeTabStyle : tabStyle}>
                {contentMap[key].title}
              </div>
            ))}
          </div>
          <div style={{ padding: "10px" }}>{contentMap[activeCard1Tab].content}</div>
        </div>

        {/* Card 2 */}
        <div style={mainCardStyle}>
          <div style={tabContainerStyle}>
            {card2Tabs.map(key => (
              <div key={key} onClick={() => setActiveCard2Tab(key)} style={activeCard2Tab === key ? activeTabStyle : tabStyle}>
                {contentMap[key].title}
              </div>
            ))}
          </div>
          <div style={{ padding: "10px" }}>{contentMap[activeCard2Tab].content}</div>
        </div>

        {/* Card 3 */}
        <div style={mainCardStyle}>
          <div style={tabContainerStyle}>
            {card3Tabs.map(key => (
              <div key={key} onClick={() => setActiveCard3Tab(key)} style={activeCard3Tab === key ? activeTabStyle : tabStyle}>
                {contentMap[key].title}
              </div>
            ))}
          </div>
          <div style={{ padding: "10px" }}>{contentMap[activeCard3Tab].content}</div>
        </div>
      </div>
    </div>
  );
};

export default HistoricalReportModal;
